// Adapter for Henry's api_v2/item/Main_resource/* endpoints.
// Target backend branch: feature/migrate_mc_question (HEAD 053a5000 as of
// 2026-05-27). Replaces the prior placeholder that called fictional
// /api/admin/Main_resource/mc_question/* paths.
//
// Envelope: { result: 1|0|-1|2|3|4, ret: T }. Non-1 results surface as
// MainResourceError so the editor can render a controlled toast.
//
// Types live in main_resource.types.ts; transformers (vo_lang, topic_id)
// live there too. This file is the network boundary only.

import axios from 'axios';
import { getLang, getMID, getUToken, getUUID } from 'lib/evi-api-gateway';
import {
  AddResourcePayload,
  AddResourceResponse,
  EditorialListFilters,
  EditorialListResponse,
  FlatTopicListResponse,
  FlatTopicRow,
  FullInfoResponse,
  ListControl,
  MainResourceEnvelope,
  MainResourceError,
  MainResourceResultCode,
  OssCallbackAnswerParams,
  OssSignUrlAnswerParams,
  OssSignUrlAnswerSoundParams,
  OssSignUrlChineseWordAudioParams,
  OssSignUrlMaterialParams,
  OssSignUrlResponse,
  SaveAllPayload,
  SaveAllResponse,
  SetChineseWordAudioIdParams,
  TaxonomyTreeNode,
  TopicType,
  describeResultCode
} from './main_resource.types';

const RESOURCE_ROOT = process.env.REACT_APP_API_URL_RESOURCE;
const MAIN_RESOURCE_BASE = `${RESOURCE_ROOT}/api_v2/item/Main_resource`;
const TOPIC_TYPE_BASE = `${RESOURCE_ROOT}/api/item/mc_question_admin_topic_type`;
// Henry's FINALIZED flat topic taxonomy controller. Sibling of main_resource
// under api_v2/item, so expected to share its CORS allowlist (verify live).
const TOPIC_TYPE_V2_BASE = `${RESOURCE_ROOT}/api_v2/item/topic_type`;

type MainResourcePayload = Record<string, unknown>;

function unwrap<T>(envelope: MainResourceEnvelope<T>): T {
  if (!envelope || envelope.result !== MainResourceResultCode.Successful) {
    const code = envelope?.result ?? MainResourceResultCode.Failed;
    // eslint-disable-next-line no-console
    console.warn(
      '[main_resource] non-success result',
      code,
      describeResultCode(code),
      envelope?.data ?? envelope?.ret
    );
    throw new MainResourceError(code, envelope?.data ?? envelope?.ret);
  }
  // Henry returns the payload under `data` (verified 2026-05-28); some
  // earlier Postman docs called it `ret`. Prefer data, fall back to ret.
  return (envelope.data !== undefined ? envelope.data : (envelope.ret as T)) as T;
}

// Direct axios POST with form-data + auth params injected. Bypasses
// `client.post` because its `_successCallback` strips the `{result, ret}`
// envelope down to just the numeric result, losing all `ret` payload.
// Henry's controllers read `$this->input->post('access_token')` etc, so
// the request must be application/x-www-form-urlencoded (or multipart),
// not JSON — `client.post_v2` sends JSON which Henry can't parse for auth.
async function postForm<T>(url: string, data: MainResourcePayload): Promise<T> {
  const params = new URLSearchParams();
  const auth: Record<string, string> = {
    lang: String(getLang() || ''),
    device_id: String(getUUID() || ''),
    access_token: String(getUToken() || ''),
    merchant_id: String(getMID() || '')
  };
  Object.entries(auth).forEach(([k, v]) => {
    if (v) params.append(k, v);
  });
  Object.entries(data).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    params.append(k, typeof v === 'string' ? v : JSON.stringify(v));
  });
  const response = await axios.post<MainResourceEnvelope<T>>(url, params.toString(), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  });
  // Debug surface — gated to non-production so the hot path doesn't leak full
  // envelopes (content + checksum + signed OSS URLs) on every request in prod.
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug(
      '[main_resource]',
      url.replace(MAIN_RESOURCE_BASE, '').replace(TOPIC_TYPE_BASE, ''),
      response.data
    );
  }
  return unwrap<T>(response.data);
}

function postMain<T>(path: string, data: MainResourcePayload = {}): Promise<T> {
  return postForm<T>(`${MAIN_RESOURCE_BASE}/${path}`, data);
}

function postTopic<T>(path: string, data: MainResourcePayload = {}): Promise<T> {
  return postForm<T>(`${TOPIC_TYPE_BASE}/${path}`, data);
}

function postTopicTypeV2<T>(path: string, data: MainResourcePayload = {}): Promise<T> {
  return postForm<T>(`${TOPIC_TYPE_V2_BASE}/${path}`, data);
}

// ── Editorial list (§1.2 — shipped commit 9bd18b19) ─────────────────────

export function getEditorialList(
  filters: EditorialListFilters = {},
  control: ListControl = {}
): Promise<EditorialListResponse> {
  return postMain<EditorialListResponse>('get_editorial_list', { ...filters, ...control });
}

// ── Full info (shipped earlier, optimised in 5a609ec7) ──────────────────

export function getFullInfo(params: { id: number }): Promise<FullInfoResponse> {
  return postMain<FullInfoResponse>('get_full_info', params);
}

// ── Create draft (Henry endpoint name = add_math_question after f970d39a) ─

export function addResource(payload: AddResourcePayload): Promise<AddResourceResponse> {
  // Endpoint renamed from add_resource → add_math_question in commit f970d39a.
  // Response ret stripped to just { adapter_id }; callers must follow up with
  // get_full_info(adapter_id) to recover res_id, resource_item_id, answer_ids.
  //
  // Contract fix C2: `type` used to be stripped from this signature, so Chinese
  // word-bank templates could never be created through `addMcChineseBundle`.
  // which requires `type: 'mc_chinese'`. Callers now pass `type` explicitly.
  return postMain<AddResourceResponse>('add_math_question', {
    name: '未命名題目',
    difficulty: '1',
    target_grade: 'k2',
    question_type_id: 1,
    topic_id: 1,
    question_program: 'godot',
    publish_status: 'DRAFT',
    ...payload
  });
}

// ── Save-all (folded answers + §1.1 editorial_status + §1.5 section_meta)─

// Flatten a nested value into PHP array-notation form pairs, e.g.
//   toPhpFormPairs('answers[0]', {id: 5, lang_data: [{lang:'zh'}]}, out)
//     → answers[0][id]=5, answers[0][lang_data][0][lang]=zh
// Henry's save_all reads `$this->input->post('answers')` and checks
// is_array() — CodeIgniter only yields an array when the form body uses this
// bracket notation, NOT when `answers` is a JSON string. Verified against UAT
// 2026-05-29: a JSON-string `answers` trips ANSWERS_REQUIRED (result=0); the
// bracket form below is parsed as the expected array (matches Henry's
// finalized Postman collection §1.4).
function toPhpFormPairs(prefix: string, value: unknown, out: Array<[string, string]>): void {
  if (Array.isArray(value)) {
    value.forEach((v, i) => toPhpFormPairs(`${prefix}[${i}]`, v, out));
  } else if (value !== null && typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) =>
      toPhpFormPairs(`${prefix}[${k}]`, v, out)
    );
  } else if (value !== undefined && value !== null) {
    out.push([prefix, String(value)]);
  }
}

export function saveAll(payload: SaveAllPayload): Promise<SaveAllResponse> {
  // section_meta may arrive as object; Henry accepts both string and object
  // forms but stringifies via JSON_UNESCAPED_UNICODE for storage. Pre-string
  // here so the wire payload is deterministic across browsers.
  const wire: MainResourcePayload = {
    ...payload,
    section_meta:
      typeof payload.section_meta === 'string'
        ? payload.section_meta
        : payload.section_meta
        ? JSON.stringify(payload.section_meta)
        : undefined
  };
  // answers must go over the wire as PHP array notation, not a JSON string
  // (see toPhpFormPairs). Expand into answers[i][...] keys and drop the array
  // so postForm doesn't JSON.stringify it.
  delete wire.answers;
  const answerPairs: Array<[string, string]> = [];
  (payload.answers || []).forEach((answer, i) =>
    toPhpFormPairs(`answers[${i}]`, answer, answerPairs)
  );
  answerPairs.forEach(([k, v]) => {
    wire[k] = v;
  });
  return postMain<SaveAllResponse>('save_all', wire);
}

// ── OSS sign-URL endpoints (six asset types, shipped earlier) ────────────

export function ossSignUrlImage(params: OssSignUrlMaterialParams): Promise<OssSignUrlResponse> {
  return postMain<OssSignUrlResponse>('oss_signurl_image', toOssWire(params));
}

export function ossSignUrlBackground(
  params: OssSignUrlMaterialParams
): Promise<OssSignUrlResponse> {
  return postMain<OssSignUrlResponse>('oss_signurl_background', toOssWire(params));
}

export function ossSignUrlSound(params: OssSignUrlMaterialParams): Promise<OssSignUrlResponse> {
  return postMain<OssSignUrlResponse>('oss_signurl_sound', toOssWire(params));
}

export function ossSignUrlVideo(params: OssSignUrlMaterialParams): Promise<OssSignUrlResponse> {
  return postMain<OssSignUrlResponse>('oss_signurl_video', toOssWire(params));
}

export function ossSignUrlAnswer(params: OssSignUrlAnswerParams): Promise<OssSignUrlResponse> {
  return postMain<OssSignUrlResponse>('oss_signurl_answer', {
    ...params,
    meta: JSON.stringify(params.meta)
  });
}

function ossCallbackErrorCode(body: unknown): number {
  if (body && typeof body === 'object' && 'result' in body) {
    const result = Number((body as { result?: unknown }).result);
    if (Number.isFinite(result)) {
      return result;
    }
  }
  return MainResourceResultCode.ActionUnsuccessful;
}

function isOssCallbackSuccess(status: number, body: unknown): boolean {
  if (status !== 200) {
    return false;
  }
  if (body === 'error' || (typeof body === 'string' && body.trim() === 'error')) {
    return false;
  }
  if (body && typeof body === 'object' && 'result' in body) {
    const result = (body as { result?: unknown }).result;
    if (result !== undefined && Number(result) !== MainResourceResultCode.Successful) {
      return false;
    }
  }
  return true;
}

/**
 * Clear a persisted MC answer-option image without deleting the answer row.
 * This endpoint does NOT go through _AOPCall — success is often the raw
 * string `done`, not `{ result: 1, data: … }`. Do not route through unwrap().
 */
export async function ossCallbackAnswer(params: OssCallbackAnswerParams): Promise<void> {
  const form = new URLSearchParams();
  const auth: Record<string, string> = {
    lang: String(getLang() || ''),
    device_id: String(getUUID() || ''),
    access_token: String(getUToken() || ''),
    merchant_id: String(getMID() || '')
  };
  Object.entries(auth).forEach(([k, v]) => {
    if (v) form.append(k, v);
  });
  form.append('answer_id', String(params.answer_id));
  form.append('action', params.action);
  form.append('lang', params.lang || 'zh');

  const response = await axios.post(`${MAIN_RESOURCE_BASE}/oss_callback_answer`, form.toString(), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    transformResponse: [
      (raw: unknown) => {
        if (typeof raw !== 'string') {
          return raw;
        }
        const trimmed = raw.trim();
        if (!trimmed) {
          return trimmed;
        }
        try {
          return JSON.parse(trimmed);
        } catch {
          return trimmed;
        }
      }
    ]
  });

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[main_resource]', '/oss_callback_answer', response.status, response.data);
  }

  if (!isOssCallbackSuccess(response.status, response.data)) {
    throw new MainResourceError(ossCallbackErrorCode(response.data), response.data);
  }
}

export function ossSignUrlAnswerSound(
  params: OssSignUrlAnswerSoundParams
): Promise<OssSignUrlResponse> {
  return postMain<OssSignUrlResponse>('oss_signurl_answer_sound', {
    ...params,
    meta: JSON.stringify(params.meta)
  });
}

export function ossSignUrlChineseWordAudio(
  params: OssSignUrlChineseWordAudioParams
): Promise<OssSignUrlResponse> {
  return postMain<OssSignUrlResponse[]>('oss_signurl_chinese_word_audio', {
    ...params,
    meta: JSON.stringify(params.meta)
  }).then((entries) => {
    if (!Array.isArray(entries) || !entries[0]) {
      throw new MainResourceError(
        MainResourceResultCode.ActionUnsuccessful,
        'oss_signurl_chinese_word_audio returned no upload token'
      );
    }
    const token = entries[0];
    if (!token.sign_url) {
      // Without this the caller passes `undefined` to putSignedOssUrl and the
      // editor surfaces an opaque TypeError instead of a real diagnostic.
      throw new MainResourceError(
        MainResourceResultCode.ActionUnsuccessful,
        'oss_signurl_chinese_word_audio response is missing sign_url'
      );
    }
    return token;
  });
}

export function setChineseWordAudioId(params: SetChineseWordAudioIdParams): Promise<true> {
  // Empty string clears the pin (PHP treats ''/absent numeric as NULL). Do not
  // omit the key — omitting leaves the existing pin unchanged.
  const audioId = params.audio_id == null || params.audio_id === '' ? '' : params.audio_id;
  return postMain<true>('set_chinese_word_audio_id', {
    id: params.resource_item_id,
    chinese_word_audio_id: audioId
  });
}

/**
 * Pick a question-scoped resource_chinese_word_audio.id from get_full_info.
 * Never returns chinese_word_char_audio ids (dictionary / bank readings).
 */
export function pickQuestionScopedChineseWordAudioId(
  full: FullInfoResponse | null | undefined,
  preferredJyutping?: string | null
): number | null {
  if (!full?.item?.[0]) {
    return null;
  }
  const item = full.item[0];
  const pinned = Number(item.setting?.[0]?.chinese_word_audio_id);
  if (Number.isFinite(pinned) && pinned > 0) {
    return pinned;
  }

  const words =
    item.mc_chinese || item.chinese_words || full.mc_chinese || full.chinese_words || [];
  const word = words[0];
  const audios = (word?.audios || word?.audio || []).filter(
    (audio) => audio?.id != null && Number(audio.id) > 0
  );
  if (!audios.length) {
    return null;
  }

  const jyut = String(preferredJyutping || word?.jyutping || '').trim();
  const matched = jyut
    ? audios.find((audio) => String(audio.jyutping || '').trim() === jyut)
    : undefined;
  const chosen = matched || audios[0];
  const id = Number(chosen?.id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function toOssWire(params: OssSignUrlMaterialParams): MainResourcePayload {
  // Contract fix C1 — updated 2026-08-13 against evi-resource-service
  // `hotfix/question-set-code-standards`.
  //
  // The naming has since been inverted backend-side: `material_id` is now the
  // canonical wire name (it is the declared required param for
  // oss_signurl_{image,background,sound,video}) and `id` is kept only as a
  // legacy fallback — "Frontend / callback naming: material_id; accept legacy
  // `id`" (Main_resource.php:73-78).
  //
  // We spread `...params` (which carries `material_id`) and additionally set
  // `id`, so the payload satisfies both the old contract, where only `id` was
  // read, and the current one. Keep sending both until every environment is on
  // the hotfix branch; then `id` can be dropped.
  return {
    ...params,
    id: params.material_id,
    meta: JSON.stringify(params.meta)
  };
}

// ── Topic / taxonomy admin (Mc_question_admin_topic_type) ───────────────

export function getTopicList(params: MainResourcePayload = {}): Promise<TopicType[]> {
  return postTopic<TopicType[]>('get_topic_list', params);
}

// Henry moved the taxonomy read onto the api_v2 main_resource base (CORS-OK
// for the panel origin) as `get_topic_list` — verified live 2026-06-01. The
// legacy /api/item/mc_question_admin_topic_type/* base is NOT CORS-allowed for
// localhost, which is why the old getTopicTypeList('get_list') never loaded.
// The api_v2 rows use different field names than our TopicType; map them.
interface RawTopicListRow {
  id?: string | number;
  type_id?: string | number;
  name?: string;
  category?: string;
  topic?: string;
  performance_indicator?: string;
  subject?: string;
  kLevels?: string;
  domain?: string;
}

function mapTopicListRow(row: RawTopicListRow): TopicType {
  return {
    topic_type_id: Number(row.type_id ?? row.id ?? 0),
    topic: String(row.topic ?? row.name ?? ''),
    subject: row.subject ?? '',
    learning_domain: row.domain ?? '',
    learning_unit: row.category ?? '',
    topic_grade: row.kLevels ?? '',
    performance_indicator: row.performance_indicator ?? ''
  };
}

/**
 * @deprecated Superseded by `getTopicTypeFlatList` (`topic_type/get_list`).
 * Retained only as a one-line manual rollback for the topic_type migration;
 * `get_topic_list` may be removed from UAT. Do NOT auto-fall back to it — a
 * silent fallback would mask a `get_list` failure with stale data.
 */
export function getTopicTypeList(params: MainResourcePayload = {}): Promise<TopicType[]> {
  return postMain<RawTopicListRow[]>('get_topic_list', {
    page: 1,
    limit: 9999,
    ...params
  }).then((rows) => (Array.isArray(rows) ? rows.map(mapTopicListRow) : []));
}

// ── Finalized flat Topic Type API (api_v2/item/topic_type/*) ─────────────
//
// Henry's finalized topic taxonomy. `get_list` returns the whole tree as a
// flat adjacency list (`{ total, rows }`); add/update/delete are thin flat
// mutations. See FlatTopicRow. Auth params are injected by postForm.

export function getTopicTypeFlatList(
  params: MainResourcePayload = {}
): Promise<FlatTopicListResponse> {
  return postTopicTypeV2<FlatTopicListResponse>('get_list', {
    limit: 9999,
    offset: 0,
    ...params
  });
}

export interface FlatTopicAddParams {
  topic: string;
  parent_id?: string | number;
  level?: number;
  sort?: number;
  topic_grade?: string;
  performance_indicator?: string;
  subject?: string;
  learning_domain?: string;
  learning_unit?: string;
}

// Returns the inserted row id (Henry sends it as a string under `data`).
export function topicTypeFlatAdd(params: FlatTopicAddParams): Promise<string> {
  // postForm drops undefined keys, so only the provided fields go on the wire.
  const body: MainResourcePayload = {
    topic: params.topic,
    parent_id: params.parent_id,
    level: params.level,
    sort: params.sort,
    topic_grade: params.topic_grade,
    performance_indicator: params.performance_indicator,
    subject: params.subject,
    learning_domain: params.learning_domain,
    learning_unit: params.learning_unit
  };
  return postTopicTypeV2<string>('add', body);
}

export interface FlatTopicUpdateParams {
  id: string | number;
  topic?: string;
  parent_id?: string | number;
  level?: number;
  sort?: number;
  topic_grade?: string;
  performance_indicator?: string;
  subject?: string;
  learning_domain?: string;
  learning_unit?: string;
}

export function topicTypeFlatUpdate(params: FlatTopicUpdateParams): Promise<number> {
  const body: MainResourcePayload = {
    id: params.id,
    topic: params.topic,
    parent_id: params.parent_id,
    level: params.level,
    sort: params.sort,
    topic_grade: params.topic_grade,
    performance_indicator: params.performance_indicator,
    subject: params.subject,
    learning_domain: params.learning_domain,
    learning_unit: params.learning_unit
  };
  return postTopicTypeV2<number>('update', body);
}

export function topicTypeFlatDelete(params: { id: string | number }): Promise<number> {
  return postTopicTypeV2<number>('delete', params);
}

// Fetch one topic_type row by id. For a leaf this carries the denormalized
// taxonomy path (subject / learning_domain / learning_unit / topic), which is
// how fromFullInfo reverse-maps a saved topic_id back to the 學習目標 cascade
// (get_full_info no longer returns topic_type_list).
export function topicTypeFlatGet(params: { id: string | number }): Promise<FlatTopicRow> {
  return postTopicTypeV2<FlatTopicRow>('get', { id: params.id });
}

// ── Abandoned hierarchical scaffolds (Henry dropped get_tree_list) ───────
// Kept compiling but unused; routed at the non-CORS topic base. Do not use.

/** @deprecated Henry abandoned `get_tree_list`. Use getTopicTypeFlatList. */
export function getTreeList(params: { parent_id: number | 'root' }): Promise<TaxonomyTreeNode[]> {
  return postTopic<TaxonomyTreeNode[]>('get_tree_list', params);
}

/** @deprecated Use getTopicTypeFlatList. */
export function getTopicListByParent(params: { parent_id: number | 'root' }): Promise<TopicType[]> {
  return postTopic<TopicType[]>('get_topic_list_by_parent', params);
}

/** @deprecated Use topicTypeFlatAdd. */
export function topicTypeAdd(params: { topic: string }): Promise<{ topic_type_id: number }> {
  return postTopic<{ topic_type_id: number }>('add', params);
}

/** @deprecated Use a getTopicTypeFlatList lookup. */
export function topicTypeGet(params: { id: number }): Promise<TopicType> {
  return postTopic<TopicType>('get', params);
}

/** @deprecated Use topicTypeFlatUpdate. */
export function topicTypeUpdate(
  params: { id: number } & Partial<Omit<TopicType, 'topic_type_id'>>
): Promise<true> {
  return postTopic<true>('update', params);
}

/** @deprecated Use topicTypeFlatDelete. */
export function topicTypeDelete(params: { id: number }): Promise<true> {
  return postTopic<true>('delete', params);
}

// ── OSS direct PUT ──────────────────────────────────────────────────────
//
// Henry signs the OSS v1 StringToSign WITH the file's MIME type on the
// Content-Type line (it comes from the `meta.type` we pass to oss_signurl_*).
// Verified live against UAT 2026-05-29: PUT with `Content-Type: image/png`
// → 200 {"result":1,"data":"done"}; PUT with NO Content-Type → 403
// SignatureDoesNotMatch (OSS canonicalizes an empty line 3, Henry signed
// the populated one). So the request MUST carry Content-Type === the same
// MIME passed at sign time. (An earlier note here claimed the opposite —
// that was Henry's incorrect first guess; the live matrix disproved it.)
//
// The `mime` arg should be the exact value sent as meta.type to the
// oss_signurl_* call. Falls back to the File's own type. The callback rides
// as the `x-oss-callback` request header; the signed URL query has only
// OSSAccessKeyId/Expires/Signature.
export async function putSignedOssUrl(
  signUrl: string,
  file: File,
  xOssCallback?: string,
  mime?: string
): Promise<void> {
  const contentType = mime || file.type || 'application/octet-stream';
  const headers: Record<string, string> = { 'Content-Type': contentType };
  if (xOssCallback) {
    headers['x-oss-callback'] = xOssCallback;
  }
  const response = await fetch(signUrl, {
    method: 'PUT',
    headers,
    body: new Blob([file], { type: contentType })
  });
  if (!response.ok) {
    throw new Error(`OSS upload failed: ${response.status}`);
  }
}

// ── Legacy compat shims (throw with controlled error until callers move) ─
// Some panel code still imports these by name; keeping them as throwers
// surfaces broken usages clearly instead of failing silently against the
// new contract.

export interface UploadTokenResponse {
  sign_url?: string;
  signedUrl?: string;
  uploadUrl?: string;
  oss_url?: string;
  ossUrl?: string;
  url?: string;
  [key: string]: unknown;
}

export function getUploadToken(_params: MainResourcePayload): Promise<UploadTokenResponse> {
  return Promise.reject(
    new MainResourceError(
      MainResourceResultCode.ActionUnsuccessful,
      'getUploadToken is deprecated. Use ossSignUrl{Image,Background,Sound,Video,Answer,AnswerSound} with material_id (or answer_id) instead.'
    )
  );
}

export function createQuestion(_payload: MainResourcePayload): Promise<unknown> {
  return Promise.reject(
    new MainResourceError(
      MainResourceResultCode.ActionUnsuccessful,
      'createQuestion is deprecated. Use addResource({ type: "mc_math", ... }).'
    )
  );
}

export function updateQuestion(_payload: MainResourcePayload): Promise<unknown> {
  return Promise.reject(
    new MainResourceError(
      MainResourceResultCode.ActionUnsuccessful,
      'updateQuestion is deprecated. Use saveAll(payload) — Henry folds answers into the same endpoint.'
    )
  );
}

export function getQuestion(_payload: MainResourcePayload): Promise<unknown> {
  return Promise.reject(
    new MainResourceError(
      MainResourceResultCode.ActionUnsuccessful,
      'getQuestion is deprecated. Use getFullInfo({ id }).'
    )
  );
}

export function listQuestions(_payload: MainResourcePayload = {}): Promise<unknown> {
  return Promise.reject(
    new MainResourceError(
      MainResourceResultCode.ActionUnsuccessful,
      'listQuestions is deprecated. Use getEditorialList(filters, control).'
    )
  );
}

export function setAnswers(_payload: MainResourcePayload): Promise<unknown> {
  return Promise.reject(
    new MainResourceError(
      MainResourceResultCode.ActionUnsuccessful,
      'setAnswers is deprecated. Answers are folded into saveAll(payload).answers.'
    )
  );
}

export function transitionQuestion(_payload: MainResourcePayload): Promise<unknown> {
  // Henry chose Option B (§1.1): editorial transitions go through save_all
  // with an editorial_status field. There is no standalone transition
  // endpoint. Callers should set saveAll(payload).editorial_status instead.
  return Promise.reject(
    new MainResourceError(
      MainResourceResultCode.ActionUnsuccessful,
      'transitionQuestion is deprecated. Set editorial_status on saveAll(payload).'
    )
  );
}

export function getTaxonomy(params: MainResourcePayload = {}): Promise<TopicType[]> {
  // Surface old-name as an alias of getTopicTypeList until callers migrate.
  return getTopicTypeList(params);
}

export function createTaxonomy(params: { topic: string }): Promise<{ topic_type_id: number }> {
  return topicTypeAdd(params);
}

export function updateTaxonomy(
  params: { id: number } & Partial<Omit<TopicType, 'topic_type_id'>>
): Promise<true> {
  return topicTypeUpdate(params);
}

export function deleteTaxonomy(params: { id: number }): Promise<true> {
  return topicTypeDelete(params);
}
